import { z } from 'zod';

// ─── Auth ────────────────────────────────────────────────────

export const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'validation.emailRequired')
    .email('validation.emailInvalid'),
  password: z
    .string()
    .min(1, 'validation.passwordRequired')
    .min(8, 'validation.passwordMinLength'),
});

export const registerSchema = z
  .object({
    email: z
      .string()
      .min(1, 'validation.emailRequired')
      .email('validation.emailInvalid'),
    password: z
      .string()
      .min(1, 'validation.passwordRequired')
      .min(8, 'validation.passwordMinLength'),
    confirmPassword: z
      .string()
      .min(1, 'validation.confirmPasswordRequired'),
    displayName: z
      .string()
      .min(1, 'validation.displayNameRequired')
      .max(50, 'validation.displayNameMaxLength'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'validation.passwordsMustMatch',
    path: ['confirmPassword'],
  });

// ─── Profile ─────────────────────────────────────────────────

export const profileSchema = z.object({
  displayName: z
    .string()
    .min(1, 'validation.displayNameRequired')
    .max(50, 'validation.displayNameMaxLength'),
  bio: z
    .string()
    .max(500, 'validation.bioMaxLength')
    .optional()
    .or(z.literal('')),
  locationCity: z
    .string()
    .max(100, 'validation.cityMaxLength')
    .optional()
    .or(z.literal('')),
  locationCountry: z
    .string()
    .max(100, 'validation.countryMaxLength')
    .optional()
    .or(z.literal('')),
  experienceLevel: z
    .enum(['beginner', 'intermediate', 'advanced', 'expert'])
    .optional(),
  yearsExperience: z
    .number()
    .int()
    .min(0, 'validation.yearsExperienceMin')
    .max(99, 'validation.yearsExperienceMax')
    .optional()
    .nullable(),
});

// ─── Settings ────────────────────────────────────────────────

export const settingsAccountSchema = z.object({
  email: z
    .string()
    .min(1, 'validation.emailRequired')
    .email('validation.emailInvalid'),
  currentPassword: z
    .string()
    .min(1, 'validation.currentPasswordRequired')
    .optional()
    .or(z.literal('')),
  newPassword: z
    .string()
    .min(8, 'validation.passwordMinLength')
    .optional()
    .or(z.literal('')),
  confirmNewPassword: z
    .string()
    .optional()
    .or(z.literal('')),
}).refine((data) => {
  if (data.newPassword && !data.confirmNewPassword) return false;
  if (data.newPassword && data.newPassword !== data.confirmNewPassword) return false;
  return true;
}, {
  message: 'validation.passwordsMustMatch',
  path: ['confirmNewPassword'],
});

// ─── Password Reset ──────────────────────────────────────────

export const passwordResetSchema = z
  .object({
    newPassword: z
      .string()
      .min(1, 'validation.passwordRequired')
      .min(8, 'validation.passwordMinLength'),
    confirmPassword: z
      .string()
      .min(1, 'validation.confirmPasswordRequired'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'validation.passwordsMustMatch',
    path: ['confirmPassword'],
  });

// ─── Onboarding ──────────────────────────────────────────────

export const onboardingStep2Schema = z.object({
  instruments: z
    .array(z.string())
    .min(1, 'validation.instrumentsRequired'),
});

export const onboardingStep3Schema = z.object({
  genres: z
    .array(z.string())
    .min(1, 'validation.genresRequired'),
});

export const onboardingStep4Schema = z.object({
  city: z
    .string()
    .min(1, 'validation.cityRequired')
    .max(100, 'validation.cityMaxLength'),
  country: z
    .string()
    .min(1, 'validation.countryRequired')
    .max(100, 'validation.countryMaxLength'),
});

// ─── Project ─────────────────────────────────────────────────

export const projectBasicInfoSchema = z.object({
  name: z
    .string()
    .min(1, 'validation.projectNameRequired')
    .max(100, 'validation.projectNameMaxLength'),
  description: z
    .string()
    .min(1, 'validation.projectDescriptionRequired')
    .max(2000, 'validation.projectDescriptionMaxLength'),
  projectType: z.enum(['band', 'recording', 'concert', 'jam_session']).optional(),
  location: z
    .string()
    .max(200, 'validation.locationMaxLength')
    .optional()
    .or(z.literal('')),
  objectives: z
    .string()
    .max(1000, 'validation.objectivesMaxLength')
    .optional()
    .or(z.literal('')),
});

// ─── Create Gig ──────────────────────────────────────────────

export const createGigSchema = z.object({
  title: z
    .string()
    .min(1, 'validation.gigTitleRequired')
    .max(200, 'validation.gigTitleMaxLength'),
  description: z
    .string()
    .min(1, 'validation.gigDescriptionRequired')
    .max(2000, 'validation.gigDescriptionMaxLength'),
  gigType: z
    .string()
    .min(1, 'validation.gigTypeRequired'),
  date: z
    .date({ required_error: 'validation.gigDateRequired' }),
  durationHours: z
    .number()
    .min(0.5, 'validation.gigDurationMin')
    .max(24, 'validation.gigDurationMax')
    .optional()
    .nullable(),
  budgetMin: z
    .number()
    .min(0, 'validation.budgetMin')
    .optional()
    .nullable(),
  budgetMax: z
    .number()
    .min(0, 'validation.budgetMax')
    .optional()
    .nullable(),
});

// ─── Create Venue ────────────────────────────────────────────

export const createVenueSchema = z.object({
  name: z
    .string()
    .min(1, 'validation.venueNameRequired')
    .max(200, 'validation.venueNameMaxLength'),
  description: z
    .string()
    .max(2000, 'validation.venueDescriptionMaxLength')
    .optional()
    .or(z.literal('')),
  location: z
    .string()
    .min(1, 'validation.venueLocationRequired')
    .max(200, 'validation.venueLocationMaxLength'),
  capacity: z
    .number()
    .int()
    .min(1, 'validation.venueCapacityMin')
    .optional()
    .nullable(),
  venueType: z
    .string()
    .min(1, 'validation.venueTypeRequired'),
});

// ─── Application ─────────────────────────────────

export const applicationSchema = z.object({
  motivation_letter: z
    .string()
    .min(20, 'validation.motivationLetterMin')
    .max(2000, 'validation.motivationLetterMax'),
  portfolio_url: z
    .string()
    .url('validation.urlInvalid')
    .optional()
    .or(z.literal('')),
  audition_type: z.enum(['none', 'virtual', 'in_person', 'recorded']),
});

// ─── Type exports ────────────────────────────────────────────

export type LoginFormValues = z.infer<typeof loginSchema>;
export type RegisterFormValues = z.infer<typeof registerSchema>;
export type ProfileFormValues = z.infer<typeof profileSchema>;
export type SettingsAccountFormValues = z.infer<typeof settingsAccountSchema>;
export type PasswordResetFormValues = z.infer<typeof passwordResetSchema>;
export type OnboardingStep2Values = z.infer<typeof onboardingStep2Schema>;
export type OnboardingStep3Values = z.infer<typeof onboardingStep3Schema>;
export type OnboardingStep4Values = z.infer<typeof onboardingStep4Schema>;
export type ProjectBasicInfoValues = z.infer<typeof projectBasicInfoSchema>;
export type CreateGigFormValues = z.infer<typeof createGigSchema>;
export type CreateVenueFormValues = z.infer<typeof createVenueSchema>;